// src/components/coordinateGrid/CoordinateGridView.tsx
/**
 * CoordinateGridView Component
 * 
 * Main canvas component for the 2D coordinate grid view mode.
 * Integrates all grid, axes, and interaction layers.
 * 
 * Features:
 * - Configurable background grid
 * - X and Y axes with ticks and labels
 * - Pan and zoom support (integrated with existing viewport system)
 * - Quadrant visibility control
 * - Real-time coordinate display
 * 
 * Phase 1: Core infrastructure
 */

import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../types/redux';
import { pan, zoomAt, centerOrigin } from '../../store/slices/viewportSlice';
import { selectActiveTool } from '../../store/slices/toolModeSlice';
import { GridRenderer } from './GridRenderer';
import { AxesRenderer } from './AxesRenderer';
import { CoordinateDisplay } from './CoordinateDisplay';

interface CoordinateGridViewProps {
  /** SVG width in pixels */
  width: number;
  /** SVG height in pixels */
  height: number;
  /** Additional CSS class name */
  className?: string;
  /** ID for the SVG element */
  id?: string;
  /** Ref for export functionality */
  exportRef?: React.RefObject<HTMLDivElement>;
}

// Zoom configuration
const MIN_SCALE = 20;
const MAX_SCALE = 500;
const ZOOM_FACTOR = 1.1;

export const CoordinateGridView: React.FC<CoordinateGridViewProps> = ({
  width,
  height,
  className = '',
  id = 'coordinate-grid',
  exportRef,
}) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // State from Redux
  const viewport = useSelector((state: RootState) => state.viewport);
  const coordinateGrid = useSelector((state: RootState) => state.coordinateGrid);
  const preferences = useSelector((state: RootState) => state.preferences);
  const activeTool = useSelector(selectActiveTool);

  // Local state for interactions
  const [isPanning, setIsPanning] = useState(false);
  const [lastPointerPosition, setLastPointerPosition] = useState<{ x: number; y: number } | null>(null);

  // Dark mode
  const isDark = preferences.theme === 'dark';

  // Use equal scaling if enabled
  const scaleX = viewport.scale;
  const scaleY = coordinateGrid.equalScaling ? viewport.scale : (viewport.scale);
  const offsetX = viewport.offsetX;
  const offsetY = viewport.offsetY;

  // Merge container ref with export ref
  const mergedRef = useCallback(
    (el: HTMLDivElement | null) => {
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      if (exportRef && 'current' in exportRef) {
        (exportRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }
    },
    [exportRef]
  );

  // =========================================================================
  // Pan & Zoom Handlers
  // =========================================================================

  /**
   * Handle pointer down for panning
   */
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Only pan with primary button and pan tool
      if (e.button !== 0 || activeTool !== 'pan') return;

      e.preventDefault();
      setIsPanning(true);
      setLastPointerPosition({ x: e.clientX, y: e.clientY });
      
      // Capture pointer for smooth dragging
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    },
    [activeTool]
  );

  /**
   * Handle pointer move for panning
   */
  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isPanning || !lastPointerPosition) return;

      const deltaX = e.clientX - lastPointerPosition.x;
      const deltaY = e.clientY - lastPointerPosition.y;

      dispatch(pan({ deltaX, deltaY }));
      setLastPointerPosition({ x: e.clientX, y: e.clientY });
    },
    [isPanning, lastPointerPosition, dispatch]
  );

  /**
   * Handle pointer up to end panning
   */
  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      setIsPanning(false);
      setLastPointerPosition(null);
      (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    },
    []
  );

  /**
   * Handle wheel for zooming
   */
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      // Require Ctrl/Cmd for zoom
      if (!e.ctrlKey && !e.metaKey) {
        // Without modifier, scroll to pan
        dispatch(pan({ deltaX: -e.deltaX, deltaY: -e.deltaY }));
        return;
      }

      e.preventDefault();

      // Get mouse position relative to SVG
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate new scale
      const direction = e.deltaY < 0 ? 1 : -1;
      const newScale = Math.max(
        MIN_SCALE,
        Math.min(MAX_SCALE, viewport.scale * Math.pow(ZOOM_FACTOR, direction))
      );

      // Zoom at mouse position
      dispatch(zoomAt({ scale: newScale, anchorX: mouseX, anchorY: mouseY }));
    },
    [dispatch, viewport.scale]
  );

  /**
   * Center the origin in the viewport
   */
  const handleCenterOrigin = useCallback(() => {
    dispatch(centerOrigin({ width, height }));
  }, [dispatch, width, height]);

  // =========================================================================
  // Keyboard Shortcuts
  // =========================================================================

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if we're focused on an input
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      // Center origin with 'O' or '0'
      if (e.key === 'o' || e.key === 'O' || e.key === '0') {
        handleCenterOrigin();
        e.preventDefault();
      }

      // Reset zoom with 'R'
      if (e.key === 'r' || e.key === 'R') {
        dispatch(zoomAt({ scale: 50, anchorX: width / 2, anchorY: height / 2 }));
        handleCenterOrigin();
        e.preventDefault();
      }

      // Zoom in with '+' or '='
      if (e.key === '+' || e.key === '=') {
        const newScale = Math.min(MAX_SCALE, viewport.scale * ZOOM_FACTOR);
        dispatch(zoomAt({ scale: newScale, anchorX: width / 2, anchorY: height / 2 }));
        e.preventDefault();
      }

      // Zoom out with '-'
      if (e.key === '-') {
        const newScale = Math.max(MIN_SCALE, viewport.scale / ZOOM_FACTOR);
        dispatch(zoomAt({ scale: newScale, anchorX: width / 2, anchorY: height / 2 }));
        e.preventDefault();
      }

      // Pan with arrow keys
      const panAmount = e.shiftKey ? 100 : 20;
      if (e.key === 'ArrowLeft') {
        dispatch(pan({ deltaX: panAmount, deltaY: 0 }));
        e.preventDefault();
      }
      if (e.key === 'ArrowRight') {
        dispatch(pan({ deltaX: -panAmount, deltaY: 0 }));
        e.preventDefault();
      }
      if (e.key === 'ArrowUp') {
        dispatch(pan({ deltaX: 0, deltaY: panAmount }));
        e.preventDefault();
      }
      if (e.key === 'ArrowDown') {
        dispatch(pan({ deltaX: 0, deltaY: -panAmount }));
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, viewport.scale, width, height, handleCenterOrigin]);

  // =========================================================================
  // Cursor Style
  // =========================================================================

  const cursorStyle = useMemo(() => {
    if (activeTool === 'pan') {
      return isPanning ? 'grabbing' : 'grab';
    }
    return 'crosshair';
  }, [activeTool, isPanning]);

  // =========================================================================
  // Background Color
  // =========================================================================

  const backgroundColor = isDark ? '#111827' : '#FFFFFF';

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <div
      ref={mergedRef}
      className={`coordinate-grid-container ${className}`}
      style={{
        width,
        height,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor,
        userSelect: 'none',
      }}
      data-coordinate-grid-container
    >
      {/* SVG Canvas */}
      <svg
        ref={svgRef}
        id={id}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="coordinate-grid-svg"
        style={{
          display: 'block',
          cursor: cursorStyle,
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onWheel={handleWheel}
      >
        {/* Background */}
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill={backgroundColor}
          className="grid-background"
        />

        {/* Grid Layer */}
        {coordinateGrid.showGrid && (
          <GridRenderer
            width={width}
            height={height}
            scaleX={scaleX}
            scaleY={scaleY}
            offsetX={offsetX}
            offsetY={offsetY}
            isDark={isDark}
          />
        )}

        {/* Axes Layer */}
        <AxesRenderer
          width={width}
          height={height}
          scaleX={scaleX}
          scaleY={scaleY}
          offsetX={offsetX}
          offsetY={offsetY}
          isDark={isDark}
        />

        {/* Plot Layer - placeholder for future phases */}
        <g className="plot-layer">
          {/* Equations, points, shapes will be rendered here */}
        </g>
      </svg>

      {/* Coordinate Display Overlay */}
      <CoordinateDisplay
        width={width}
        height={height}
        scaleX={scaleX}
        scaleY={scaleY}
        offsetX={offsetX}
        offsetY={offsetY}
        containerRef={containerRef}
        isDark={isDark}
      />

      {/* Pan hint (shown briefly on first load) */}
      {activeTool === 'pan' && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '6px 12px',
            borderRadius: 6,
            backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.95)',
            border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
            fontSize: 12,
            color: isDark ? '#9CA3AF' : '#6B7280',
            pointerEvents: 'none',
            opacity: 0.8,
            zIndex: 50,
          }}
        >
          Drag to pan • Ctrl+Scroll to zoom • Press O to center
        </div>
      )}

      {/* Styles */}
      <style>{`
        .coordinate-grid-container {
          touch-action: none;
        }
        
        .coordinate-grid-svg {
          touch-action: none;
        }
        
        .grid-line {
          pointer-events: none;
        }
        
        .grid-dot {
          pointer-events: none;
        }
        
        .axis-line {
          pointer-events: none;
        }
        
        .tick-label {
          pointer-events: none;
          user-select: none;
        }
        
        .axis-label {
          pointer-events: none;
          user-select: none;
        }
      `}</style>
    </div>
  );
};

export default CoordinateGridView;
